/**
 * Custom JavaScript for WTB theme
 */

/**
 * Custom Show Specs Button Component
 * 
 * A custom HTML element that scrolls to and activates the specs tab/accordion.
 * 
 * Usage:
 *   <custom-show-specs-button>
 *     <button type="button" class="link">Show specs</button>
 *   </custom-show-specs-button>
 * 
 * On click, scrolls to the product tabs section and:
 * - Desktop: activates the specs tab (targets #product-specs-tab-button)
 * - Mobile: expands the specs accordion (targets #product-specs-accordion-wrapper)
 */
class CustomShowSpecsButton extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  handleClick(event) {
    event.preventDefault();
    
    // Check if we're on mobile (corresponding to theme / product tabs component break point)
    const isMobile = window.innerWidth < 700;
    
    if (isMobile) {
      this.showSpecsMobile();
    } else {
      this.showSpecsDesktop();
    }
  }

  showSpecsDesktop() {
    // Use data attribute instead of ID, since x-tabs component overwrites button IDs
    const specsTabButton = document.querySelector('[data-specs-tab]');
    
    if (!specsTabButton) {
      console.warn('CustomShowSpecsButton: Specs tab button not found');
      return;
    }
    
    // Find the parent x-tabs element
    const tabsContainer = specsTabButton.closest('x-tabs');
    
    if (!tabsContainer) {
      console.warn('CustomShowSpecsButton: Tabs container not found');
      return;
    }
    
    // Scroll to the tabs container with some offset
    this.scrollToElement(tabsContainer, () => {
      // Click the specs tab button to activate it
      // The x-tabs component handles the tab switching on click
      specsTabButton.click();
    });
  }

  showSpecsMobile() {
    const specsAccordionWrapper = document.getElementById('product-specs-accordion-wrapper');
    
    if (!specsAccordionWrapper) {
      console.warn('CustomShowSpecsButton: Specs accordion wrapper not found');
      return;
    }
    
    // Find the accordion-disclosure element
    const accordionDisclosure = specsAccordionWrapper.querySelector('accordion-disclosure');
    
    if (!accordionDisclosure) {
      console.warn('CustomShowSpecsButton: Accordion disclosure not found');
      return;
    }
    
    // Scroll to the accordion
    this.scrollToElement(specsAccordionWrapper, () => {
      // Open the accordion using the component's API
      // The AccordionDisclosure component has an open() method
      if (typeof accordionDisclosure.open === 'function') {
        accordionDisclosure.open();
      } else {
        // Fallback: click the summary to toggle
        const summary = accordionDisclosure.querySelector('summary');
        if (summary) {
          const details = accordionDisclosure.querySelector('details');
          if (details && !details.open) {
            summary.click();
          }
        }
      }
    });
  }

  scrollToElement(element, callback) {
    // Get header height for offset (if sticky header exists)
    const header = document.querySelector('.shopify-section-group-header-group');
    const headerHeight = header ? header.offsetHeight : 0;
    
    const elementRect = element.getBoundingClientRect();
    const absoluteElementTop = elementRect.top + window.pageYOffset;
    const offsetPosition = absoluteElementTop - headerHeight - 20; // 20px extra padding
    
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
    
    // Execute callback after scroll animation (approximate)
    if (callback) {
      setTimeout(callback, 500);
    }
  }
}

// Register the custom element
if (!window.customElements.get('custom-show-specs-button')) {
  window.customElements.define('custom-show-specs-button', CustomShowSpecsButton);
}

/**
 * Custom Progress Bar Component
 * 
 * A custom HTML element that automatically syncs with its parent free-shipping-bar
 * to display progress visualization. Observes threshold and total-price attributes
 * and updates the progress indicator accordingly.
 */
class CustomProgressBar extends HTMLElement {
  constructor() {
    super();
    this._observer = null;
    this._parentBar = null;
  }

  connectedCallback() {
    // Set ARIA role
    this.setAttribute('role', 'progressbar');
    this.setAttribute('aria-valuemin', '0');
    
    // Find parent free-shipping-bar element
    this._parentBar = this.closest('free-shipping-bar');
    
    if (!this._parentBar) {
      console.warn('custom-progress-bar must be a child of free-shipping-bar');
      return;
    }
    
    // Set up MutationObserver to watch parent for attribute changes
    this._observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes') {
          const attributeName = mutation.attributeName;
          
          if (attributeName === 'threshold') {
            this._updateMaxValue();
          } else if (attributeName === 'total-price') {
            this._updateCurrentValue();
          }
        }
      });
    });
    
    // Start observing parent
    this._observer.observe(this._parentBar, {
      attributes: true,
      attributeFilter: ['threshold', 'total-price']
    });
    
    // Trigger initial update
    this._updateMaxValue();
    this._updateCurrentValue();
  }

  disconnectedCallback() {
    // Clean up observer
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    this._parentBar = null;
  }

  /**
   * Update the maximum value (threshold) from parent
   * @private
   */
  _updateMaxValue() {
    if (!this._parentBar) return;
    
    const threshold = this._parentBar.getAttribute('threshold');
    if (threshold) {
      this.setAttribute('aria-valuemax', threshold);
      this._updateProgress();
    }
  }

  /**
   * Update the current value (total price) from parent
   * @private
   */
  _updateCurrentValue() {
    if (!this._parentBar) return;
    
    const totalPrice = this._parentBar.getAttribute('total-price');
    if (totalPrice) {
      this.setAttribute('aria-valuenow', totalPrice);
      this._updateProgress();
    }
  }

  /**
   * Calculate and update the progress CSS variable
   * @private
   */
  _updateProgress() {
    const valueNow = parseFloat(this.getAttribute('aria-valuenow')) || 0;
    const valueMax = parseFloat(this.getAttribute('aria-valuemax')) || 1;
    
    // Calculate progress (0 to 1), capped at 1 (100%)
    const progress = Math.min(valueNow / valueMax, 1);
    
    // Update CSS custom property for visual display
    this.style.setProperty('--progress', progress);
  }
}

// Register the custom element
if (!window.customElements.get('custom-progress-bar')) {
  window.customElements.define('custom-progress-bar', CustomProgressBar);
}

/**
 * Custom Variant SKU Info Component
 *
 * Keeps SKU-adjacent variant metafields in sync with the active product variant.
 */
class CustomVariantSkuInfo extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.variantMap = new Map();
  }

  connectedCallback() {
    this.disconnectedCallback();
    this.abortController = new AbortController();
    this.init();
  }

  disconnectedCallback() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  init() {
    const dataEl = this.querySelector('[data-variant-sku-info]');
    const form = this.form;

    if (!dataEl) return;

    try {
      const variants = JSON.parse(dataEl.textContent || '[]');
      this.variantMap = new Map(
        variants.map((variant) => [parseInt(variant.id, 10), variant])
      );
    } catch (error) {
      return;
    }

    this.update(this.activeVariantId);

    if (form) {
      form.addEventListener('variant:change', (event) => {
        const variantId = event.detail?.variant?.id;
        this.update(variantId ? parseInt(variantId, 10) : this.activeVariantId);
      }, { signal: this.abortController.signal });
    }
  }

  get form() {
    const formId = this.dataset.productFormId;
    return formId ? document.getElementById(formId) : null;
  }

  get activeVariantId() {
    const formIdValue = this.form?.elements?.id?.value;
    const fallbackVariant = this.variantMap.keys().next().value;
    return formIdValue ? parseInt(formIdValue, 10) : fallbackVariant;
  }

  update(variantId) {
    const variant = this.variantMap.get(parseInt(variantId, 10));

    if (!variant) {
      this.setVisible(false);
      return;
    }

    const sku = this.normalizeValue(variant.sku);
    const productName = this.normalizeValue(variant.productName);
    const weight = this.normalizeValue(variant.weight);

    this.updateRow('[data-variant-sku-row]', '[data-variant-sku-value]', sku);
    this.updateRow('[data-variant-product-name-row]', '[data-variant-product-name]', productName);
    this.updateRow('[data-variant-weight-row]', '[data-variant-weight]', weight ? `${weight} g` : '');

    this.setVisible(Boolean(sku || productName || weight));
  }

  updateRow(rowSelector, valueSelector, value) {
    const row = this.querySelector(rowSelector);
    const valueEl = this.querySelector(valueSelector);

    if (!row || !valueEl) return;

    valueEl.textContent = value;
    row.hidden = !value;
  }

  normalizeValue(value) {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  }

  setVisible(isVisible) {
    this.hidden = !isVisible;

    const blockItem = this.closest('.product-info__block-item');
    if (blockItem) {
      blockItem.hidden = !isVisible;
    }
  }
}

if (!window.customElements.get('custom-variant-sku-info')) {
  window.customElements.define('custom-variant-sku-info', CustomVariantSkuInfo);
}

/**
 * Custom Variant Picker Downstream Auto-Select
 *
 * For opted-in pill selectors, when a customer creates a non-existent option combination,
 * select the first available downstream option combination. Sold-out variants still exist,
 * so they remain selected.
 */
class CustomVariantDownstreamAutoSelect extends HTMLElement {
  constructor() {
    super();
    this.variantPicker = null;
    this.availabilityData = null;
    this.handleChange = this.handleChange.bind(this);
  }

  connectedCallback() {
    this.variantPicker = this.closest('variant-picker');

    if (!this.variantPicker) {
      return;
    }

    this.variantPicker.addEventListener('change', this.handleChange, true);
  }

  disconnectedCallback() {
    if (this.variantPicker) {
      this.variantPicker.removeEventListener('change', this.handleChange, true);
    }

    this.variantPicker = null;
    this.availabilityData = null;
  }

  handleChange(event) {
    const input = event.target;

    if (!(input instanceof HTMLInputElement) || !input.matches('input[type="radio"][data-option-position]')) {
      return;
    }

    // Only apply to pill/block/swatch-style controls, not dropdown popover controls.
    if (!input.closest('.variant-picker__option-values')) {
      return;
    }

    const changedPosition = parseInt(input.getAttribute('data-option-position'), 10);
    const form = this.getVariantPickerForm(input);
    const availabilityData = this.getAvailabilityData();

    if (!Number.isFinite(changedPosition) || !form || !availabilityData?.variants?.length) {
      return;
    }

    const selectedOptionValues = this.getSelectedOptionValues(form);

    if (this.combinationExists(availabilityData.variants, selectedOptionValues)) {
      return;
    }

    const fallbackVariant = availabilityData.variants.find((variant) => {
      return variant.available && this.variantMatchesThroughPosition(variant, selectedOptionValues, changedPosition);
    });

    if (!fallbackVariant) {
      return;
    }

    this.selectDownstreamOptionValues(form, fallbackVariant.optionValues, changedPosition);
  }

  getVariantPickerForm(input) {
    const formId = this.variantPicker?.getAttribute('form-id');

    return formId ? document.forms[formId] : input.form;
  }

  getAvailabilityData() {
    if (this.availabilityData !== null) {
      return this.availabilityData;
    }

    const dataElement = this.variantPicker?.querySelector('script[data-custom-variant-availability]');

    if (!dataElement) {
      return null;
    }

    try {
      const data = JSON.parse(dataElement.textContent || '{}');

      data.variants = Array.isArray(data.variants) ? data.variants.map((variant) => ({
        ...variant,
        optionValues: Array.isArray(variant.optionValues) ? variant.optionValues.map(String) : []
      })) : [];

      this.availabilityData = data;
      return data;
    } catch (error) {
      return null;
    }
  }

  getSelectedOptionValues(form) {
    return Array.from(form.elements).reduce((selectedOptionValues, element) => {
      if (element.matches?.('input[data-option-position]:checked')) {
        selectedOptionValues.set(
          parseInt(element.getAttribute('data-option-position'), 10),
          String(element.value)
        );
      }

      return selectedOptionValues;
    }, new Map());
  }

  combinationExists(variants, selectedOptionValues) {
    return variants.some((variant) => {
      return variant.optionValues.every((optionValue, index) => {
        return selectedOptionValues.get(index + 1) === optionValue;
      });
    });
  }

  variantMatchesThroughPosition(variant, selectedOptionValues, changedPosition) {
    for (let position = 1; position <= changedPosition; position++) {
      if (selectedOptionValues.get(position) !== variant.optionValues[position - 1]) {
        return false;
      }
    }

    return true;
  }

  selectDownstreamOptionValues(form, optionValues, changedPosition) {
    const downstreamInputs = [];

    for (const [index, optionValue] of optionValues.entries()) {
      const position = index + 1;

      if (position <= changedPosition) {
        continue;
      }

      const matchingInput = Array.from(form.elements).find((element) => {
        return element.matches?.('input[type="radio"][data-option-position]')
          && parseInt(element.getAttribute('data-option-position'), 10) === position
          && String(element.value) === optionValue
          && !element.disabled;
      });

      if (!matchingInput) {
        return;
      }

      downstreamInputs.push(matchingInput);
    }

    downstreamInputs.forEach((input) => {
      input.checked = true;
    });
  }
}

if (!window.customElements.get('custom-variant-downstream-auto-select')) {
  window.customElements.define('custom-variant-downstream-auto-select', CustomVariantDownstreamAutoSelect);
}

/**
 * Fit Right Floating Bar
 *
 * Always keep the floating element visible when rendered.
 */
(function initFitRightFloatingBar() {
  const bar = document.getElementById('fit-right-floating-bar');

  if (!bar) return;
  bar.classList.add('is-visible');
})();
